import { Box, Button, Stack, Typography, Alert } from '@mui/material';
import { AxiosError } from 'axios';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { isEmpty, transform } from 'lodash';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { requisitesApi } from 'api';
import {
  PageHeader,
  RequisitesInfo,
  DataWrapper,
  FormikNumericField,
  FormControls,
  FormActions,
  FormikArray,
  CopyTextId,
  TouchErrorsOnLoad,
} from 'components';
import { QueryKey } from 'enums';
import { useMutation, usePayinRequisitesUtils, useAppHistory } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Requisites, RequisitesCustomerLimits } from 'types';

export const RequisitesCustomerLimitsPage: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.requisites.requisites_customer_limits',
  });

  const { id } = useParams();
  const queryResult = useQuery(
    [QueryKey.Requisites, id],
    () => requisitesApi.findOne(id!),
    { enabled: !!id },
  );

  return (
    <div>
      <PageHeader title={t('title')} />
      <DataWrapper queryResult={queryResult}>
        <Fragment>
          <Box sx={{ mb: 4 }}>
            <RequisitesInfo compact requisite={queryResult.data} />
          </Box>
          <RequisitesCustomerLimitsForm requisite={queryResult.data!} />
        </Fragment>
      </DataWrapper>
    </div>
  );
};

type Values = {
  applyToAllValue: number;
  customerLimits: RequisitesCustomerLimits;
  error?: string;
};

const MAX_CUSTOMERS_COUNT = 25;
const DEFAULT_LIMIT_VALUE = 2;

const ApplyToAllForm: React.FC<{
  formik: FormikProps<Values>;
}> = ({ formik }) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.requisites.requisites_customer_limits',
  });

  const applyToAll = useCallback(() => {
    const newLimits = Array(formik.values.customerLimits.length).fill(
      formik.values.applyToAllValue,
    );
    formik.setFieldValue('customerLimits', newLimits);
  }, [formik]);

  const clearAll = useCallback(() => {
    formik.setFieldValue('customerLimits', []);
  }, [formik]);

  return (
    <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
      <FormikNumericField
        name="applyToAllValue"
        decimalScale={0}
        allowNegative={false}
        min={1}
        sx={{ flex: 1 }}
      />
      <Button
        variant="outlined"
        onClick={applyToAll}
        disabled={
          formik.values.customerLimits.length === 0 ||
          !formik.values.applyToAllValue ||
          !!formik.errors.applyToAllValue
        }
      >
        {t('apply_to_all')}
      </Button>
      <Button
        variant="outlined"
        color="error"
        onClick={clearAll}
        disabled={formik.values.customerLimits.length === 0}
      >
        {t('remove_all')}
      </Button>
    </Stack>
  );
};

const CustomerLimitItem: React.FC<{
  index: number;
  customerAssignments: any[][];
}> = ({ customerAssignments, index }) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.requisites.requisites_customer_limits',
  });

  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
      <div>
        <Typography>{`${t('customer')} ${index + 1}`}</Typography>
        {!isEmpty(customerAssignments[index]) ? (
          <div className="tw-text-xs tw-italic tw-min-w-[240px] tw-text-gray-500">
            <CopyTextId id={customerAssignments[index][0]} />
            {` (${t('orders_count')}: ${customerAssignments[index][1]})`}
          </div>
        ) : (
          <div className="tw-text-xs tw-italic tw-min-w-[240px] tw-text-gray-500">
            {t('unassigned_limit')}
          </div>
        )}
      </div>
      <FormikNumericField
        name={`customerLimits[${index}]`}
        label={t('orders_limit')}
        decimalScale={0}
        allowNegative={false}
        fullWidth
      />
    </Stack>
  );
};

type Props = {
  requisite: Requisites;
};

export const RequisitesCustomerLimitsForm: React.FC<Props> = ({
  requisite,
}) => {
  const { goBack } = useAppHistory();

  const { id } = useParams();

  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.requisites.requisites_customer_limits',
  });
  const { t: tCommon } = useTranslation();

  const customerAssignmentsQueryResult = useQuery(
    [QueryKey.RequisitesCustomerAssignments, id],
    () => requisitesApi.getCustomerAssignments(id!),
    { enabled: !isEmpty(requisite.customerLimits) },
  );

  const customerAssignments = useMemo(() => {
    const limits = requisite.customerLimits || [];
    const customers = customerAssignmentsQueryResult.data || [];

    if (isEmpty(limits) || isEmpty(customers)) {
      return [];
    }

    const usedCustomerIds = new Set();
    return transform(
      limits,
      (acc, limit) => {
        let customer = null;
        let bestValue = -1;

        for (const { customerId, assignmentsCount } of customers) {
          if (!usedCustomerIds.has(customerId) && assignmentsCount <= limit) {
            if (assignmentsCount > bestValue) {
              bestValue = assignmentsCount;
              customer = [customerId, assignmentsCount];
            }
          }
        }

        if (customer) {
          acc.push(customer);
          usedCustomerIds.add(customer[0]);
        } else {
          acc.push([]);
        }
      },
      [] as any[][],
    );
  }, [requisite.customerLimits, customerAssignmentsQueryResult.data]);

  const { requisitesUrl } = usePayinRequisitesUtils();

  const initialValues: Values = useMemo(
    () => ({
      applyToAllValue: DEFAULT_LIMIT_VALUE,
      customerLimits: requisite.customerLimits || [],
      customerAssignments,
    }),
    [requisite.customerLimits, customerAssignments],
  );

  const validationSchema = useMemo(() => {
    const limitFieldSchema = Yup.number()
      .integer(tCommon('errors.natural_number'))
      .moreThan(0, tCommon('errors.natural_number'));

    return Yup.object().shape({
      applyToAllValue: limitFieldSchema,
      customerLimits: Yup.array().of(limitFieldSchema),
    });
  }, [tCommon]);

  const navigateBack = useCallback(() => {
    goBack(requisitesUrl!);
  }, [requisitesUrl, goBack]);

  const { mutate: saveCustomerLimits } = useMutation<
    Requisites,
    AxiosError,
    { id: string; customerLimits: RequisitesCustomerLimits }
  >(
    ({ id, customerLimits }) =>
      requisitesApi.updateCustomerLimits({ id, customerLimits }),
    { notifierType: 'execute' },
  );

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      formikHelpers.setSubmitting(true);
      saveCustomerLimits(
        { id: id!, customerLimits: values.customerLimits },
        {
          onSuccess: navigateBack,
          onSettled: () => formikHelpers.setSubmitting(false),
        },
      );
    },
    [id, navigateBack, saveCustomerLimits],
  );

  return (
    <Fragment>
      <div className="tw-max-w-lg">
        <Alert severity="warning" className="tw-mb-4">
          {t('edit_warning')}
        </Alert>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnMount
          validateOnChange
        >
          {(formik: FormikProps<Values>) => (
            <Form>
              <TouchErrorsOnLoad loadedValues={initialValues} />
              <FormControls>
                <ApplyToAllForm formik={formik} />
                <FormikArray
                  name="customerLimits"
                  defaultValue={DEFAULT_LIMIT_VALUE}
                  addItemLabel={
                    formik.values.customerLimits.length < MAX_CUSTOMERS_COUNT &&
                    t('add_customer')
                  }
                  removable
                  ItemComponent={(props) => (
                    <CustomerLimitItem
                      {...props}
                      customerAssignments={customerAssignments}
                    />
                  )}
                  paperPropsParams={{
                    sx: {
                      px: 2,
                      py: 2,
                      backgroundColor: 'background.default',
                    },
                  }}
                />
                {formik.values.customerLimits.length >= MAX_CUSTOMERS_COUNT && (
                  <Alert severity="info">{t('max_customer_reached')}</Alert>
                )}
                {formik.errors?.error && (
                  <Alert severity="error">{formik.errors.error}</Alert>
                )}
              </FormControls>
              <FormActions>
                <Button variant="outlined" onClick={navigateBack}>
                  {tCommon('buttons.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={formik.isSubmitting || !formik.isValid}
                >
                  {tCommon('buttons.save')}
                </Button>
              </FormActions>
            </Form>
          )}
        </Formik>
      </div>
    </Fragment>
  );
};
